import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import { Add, Delete, Edit, Save } from '@mui/icons-material';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  createUserNote,
  deleteUserNote,
  getUserNotes,
  getUserNoteTypes,
  updateUserNote,
} from '../../endpoints/usersTs';
import { UpsertUserNoteRequest, UserNote } from '../../types/userNotes';
import SPDSelector from '../../Components/SPDSelector';

type Props = {
  id: string;
};

const emptyForm: UpsertUserNoteRequest = {
  note: '',
  note_type_id: '',
  not_interested_in_transfer: false,
  open_to_social_engagement: false,
};

export default function UserNotes({ id }: Props) {
  const [form, setForm] = useState<UpsertUserNoteRequest>(emptyForm);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [showComposer, setShowComposer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: notesData,
    isLoading,
    refetch,
  } = useApiGet(['userNotes', id], getUserNotes, { enabled: !!id }, { id });
  const { data: noteTypesData } = useApiGet(
    ['userNoteTypes'],
    getUserNoteTypes,
    { enabled: !!id }
  );
  const notes = (notesData || []) as UserNote[];
  const noteTypes = (noteTypesData || []) as { id: string; name: string }[];

  const resetForm = () => {
    setForm(emptyForm);
    setEditingNoteId(null);
    setShowComposer(false);
    setError(null);
    setIsSaving(false);
  };

  const onSuccess = () => {
    resetForm();
    refetch();
  };
  const onError = (err: any) => {
    const message =
      err?.message || err?.error || 'Unable to save note. Please try again.';
    setError(message);
    setIsSaving(false);
  };

  const { mutate: mutateCreate } = useApiSend(
    createUserNote as any,
    onSuccess,
    onError
  ) as any;
  const { mutate: mutateUpdate } = useApiSend(
    updateUserNote as any,
    onSuccess,
    onError
  ) as any;
  const { mutate: mutateDelete } = useApiSend(
    deleteUserNote as any,
    onSuccess,
    onError
  ) as any;

  const needsSPD =
    form.not_interested_in_transfer || form.open_to_social_engagement;

  const canSave = useMemo(() => {
    if (!form.note.trim() || !form.note_type_id.trim()) {
      return false;
    }
    if (needsSPD && !form.spd_chapter_id?.trim()) {
      return false;
    }
    return true;
  }, [form, needsSPD]);

  const startCreate = () => {
    setEditingNoteId(null);
    setShowComposer(true);
    setForm(emptyForm);
    setError(null);
  };

  const startEdit = (note: UserNote) => {
    setEditingNoteId(note.id);
    setShowComposer(true);
    setError(null);
    setForm({
      note: note.note ?? '',
      note_type_id: note.note_type_id ?? '',
      not_interested_in_transfer: Boolean(note.not_interested_in_transfer),
      open_to_social_engagement: Boolean(note.open_to_social_engagement),
      spd_chapter_id: note.spd_chapter_id || undefined,
    });
  };

  const save = () => {
    if (!canSave) {
      setError(
        'Note text and note type are required. S/P/D chapter is required when not interested in transfer or open to social engagement is enabled.'
      );
      return;
    }
    setIsSaving(true);
    setError(null);

    const payload: UpsertUserNoteRequest = {
      ...form,
      note: form.note.trim(),
      note_type_id: form.note_type_id.trim(),
      spd_chapter_id:
        form.not_interested_in_transfer || form.open_to_social_engagement
          ? form.spd_chapter_id?.trim() || undefined
          : undefined,
    };

    if (editingNoteId) {
      mutateUpdate({ userId: id, noteId: editingNoteId, payload });
      return;
    }
    mutateCreate({ userId: id, payload });
  };

  const remove = (noteId: string) => {
    setIsSaving(true);
    setError(null);
    mutateDelete({ userId: id, noteId });
  };

  if (isLoading) {
    return <Typography>Loading notes...</Typography>;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h6">User Notes</Typography>
              <Typography variant="body2" color="text.secondary">
                Capture transfer interest, social engagement preferences, and
                contextual notes.
              </Typography>
            </Box>
            {!showComposer ? (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={startCreate}
                disabled={isSaving}
              >
                Add note
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="inherit"
                onClick={resetForm}
                disabled={isSaving}
              >
                Cancel
              </Button>
            )}
          </Stack>

          {showComposer && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Note type"
                    value={form.note_type_id}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        note_type_id: e.target.value,
                      }))
                    }
                  >
                    {noteTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Note"
                    value={form.note}
                    inputProps={{ maxLength: 65535 }}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, note: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.not_interested_in_transfer}
                        onChange={(_, checked) =>
                          setForm((prev) => ({
                            ...prev,
                            not_interested_in_transfer: checked,
                            spd_chapter_id:
                              checked || prev.open_to_social_engagement
                                ? prev.spd_chapter_id
                                : undefined,
                          }))
                        }
                      />
                    }
                    label="Not interested in transfer"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.open_to_social_engagement}
                        onChange={(_, checked) =>
                          setForm((prev) => ({
                            ...prev,
                            open_to_social_engagement: checked,
                            spd_chapter_id:
                              checked || prev.not_interested_in_transfer
                                ? prev.spd_chapter_id
                                : undefined,
                          }))
                        }
                      />
                    }
                    label="Open to social engagement"
                  />
                </Grid>
                {(form.not_interested_in_transfer ||
                  form.open_to_social_engagement) && (
                  <Grid item xs={12} md={6}>
                    <SPDSelector
                      keyId={`notes-spd-${id}`}
                      spd={form.spd_chapter_id || ''}
                      all
                      handleSpdSelect={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          spd_chapter_id: value || undefined,
                        }))
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={save}
                    disabled={!canSave || isSaving}
                  >
                    {editingNoteId ? 'Save changes' : 'Create note'}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {notes.map((note) => (
          <Grid item xs={12} md={6} key={note.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1.25}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {note.note_type_name || 'Note'}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => startEdit(note)}
                        disabled={isSaving}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => remove(note.id)}
                        disabled={isSaving}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {note.note}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {note.not_interested_in_transfer && (
                      <Chip
                        size="small"
                        label={`Not interested in transfer${
                          note.spd_chapter_name
                            ? ` (${note.spd_chapter_name})`
                            : ''
                        }`}
                      />
                    )}
                    {note.open_to_social_engagement && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`Open to social engagement${
                          note.spd_chapter_name
                            ? ` (${note.spd_chapter_name})`
                            : ''
                        }`}
                      />
                    )}
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    {note.created_at
                      ? `Created ${dayjs(note.created_at).format('MM/DD/YYYY h:mm A')}`
                      : 'Created date unavailable'}
                    {note.created_by_name ? ` by ${note.created_by_name}` : ''}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {notes.length === 0 && (
        <Typography color="text.secondary">
          No notes found for this user.
        </Typography>
      )}
    </Stack>
  );
}
